<template>
  <dashboard-layout title="Earnings" :kicker="kicker" :items="navItems">
    <div v-if="loading" class="inter-regular-family text-muted py-4">Loading earnings…</div>
    <template v-else>
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-lg-4">
          <div class="earn-stat earn-stat--teal">
            <div class="earn-stat-label inter-regular-family">Completed jobs</div>
            <div class="earn-stat-value filson-black-family">{{ count }}</div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-4">
          <div class="earn-stat earn-stat--yellow">
            <div class="earn-stat-label inter-regular-family">Total (booking prices)</div>
            <div class="earn-stat-value filson-black-family">{{ formatMoney(total) }}</div>
            <div class="earn-stat-hint inter-regular-family text-muted small">Sum of completed booking amounts assigned to you.</div>
          </div>
        </div>
        <div class="col-12 col-lg-4 d-flex align-items-stretch">
          <button type="button" class="btn btn-outline-secondary align-self-center w-100" @click="load">
            Refresh
          </button>
        </div>
      </div>

      <div class="bg-white rounded-3 border shadow-sm p-3 p-md-4">
        <div class="fw-semibold mb-3">Completed bookings</div>
        <div v-if="!rows.length" class="text-muted inter-regular-family py-4">
          No completed jobs yet. Accept an assigned appointment, then use <strong>Mark complete</strong> on the Appointments page (or ask admin to set booking status to completed).
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead class="text-muted small">
              <tr>
                <th>Booking</th>
                <th>Customer</th>
                <th>Visit</th>
                <th class="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id">
                <td class="fw-semibold">
                  #{{ r.id }}
                  <div v-if="r.website_name" class="small text-muted fw-normal">{{ r.website_name }}</div>
                </td>
                <td>
                  {{ r.customer_name || '—' }}
                  <div class="small text-muted">{{ r.customer_phone || '' }}</div>
                </td>
                <td>
                  <div>{{ formatVisitDate(r.appointment_date) }}</div>
                  <div class="small text-muted">{{ timeWindow(r) }}</div>
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(r.price) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from './DashboardLayout.vue';
import { GROOMER_DASH_NAV, SALON_DASH_NAV } from '@/utils/dashboardNav';

export default {
  name: 'PartnerEarningsPage',
  components: { DashboardLayout },
  props: {
    partnerRole: {
      type: String,
      required: true,
      validator: (v) => v === 'groomer' || v === 'salon',
    },
  },
  data() {
    return {
      loading: true,
      rows: [],
      total: 0,
      count: 0,
    };
  },
  computed: {
    navItems() {
      return this.partnerRole === 'salon' ? SALON_DASH_NAV : GROOMER_DASH_NAV;
    },
    kicker() {
      return this.partnerRole === 'salon' ? 'Salon dashboard' : 'Groomer dashboard';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AED' }).format(n);
      } catch (_) {
        return `${n.toFixed(2)} AED`;
      }
    },
    formatVisitDate(dateStr) {
      if (!dateStr) return '—';
      const raw = String(dateStr).trim();
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
      }
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    },
    timeWindow(r) {
      const parts = [r.duration_from, r.duration_to].filter(Boolean);
      return parts.length ? parts.join(' – ') : '';
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('/partner/earnings');
        this.rows = Array.isArray(res?.data?.bookings) ? res.data.bookings : [];
        this.total = Number(res?.data?.total_earnings) || 0;
        this.count = Number(res?.data?.count) || this.rows.length;
      } catch (_) {
        this.rows = [];
        this.total = 0;
        this.count = 0;
        this.$toast?.error?.('Could not load earnings.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.earn-stat {
  border-radius: 14px;
  padding: 14px 14px 12px;
  color: #111;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 100px;
}
.earn-stat-label {
  font-size: 12px;
  opacity: 0.9;
}
.earn-stat-value {
  font-size: 24px;
  line-height: 1.15;
  margin-top: 6px;
}
.earn-stat-hint {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.35;
}
.earn-stat--teal {
  background: #02b5ad;
  color: #fff;
}
.earn-stat--yellow {
  background: #ffcc00;
}

@media (max-width: 576px) {
  .earn-stat {
    padding: 12px 12px 10px;
    min-height: 88px;
  }
  .earn-stat-value {
    font-size: 22px;
  }
}
</style>
